import { FC, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import styles from './index.module.scss'
import { CompanyReferenceStatus, ICompanyBase, ICompanyReferenceBase, MessageType } from '../../../types'
import { CompanyReferenceCard } from '../../molecules/CompanyReferenceCard'
import { useStores } from '../../../utils'
import { CompanyReferenceForm } from '../../molecules/CompanyReferenceForm'

type Props = {
  company: ICompanyBase
}

export const CompanyReferencesGroup: FC<Props> = observer(({ company }) => {
  const { t } = useTranslation(['p'])
  const { messages, viewer } = useStores()
  const [references, setReferences] = useState<ICompanyReferenceBase[]>(company.companyReferences?.nodes)
  const [editingReference, setEditingReference] = useState<ICompanyReferenceBase>(null)
  const newFormRef = useRef<HTMLDivElement>()
  const scroll = () => newFormRef.current.scrollIntoView({ behavior: 'smooth' })
  const viewerComment = references.find((r) => r.commenter.username === viewer.viewer?.username)
  const commentable = viewerComment ? viewerComment.status === CompanyReferenceStatus.SHOWN && editingReference : true
  const hiddenReferences = company.companyReferences?.nodes?.filter((r) => r.status === CompanyReferenceStatus.HIDDEN)

  const onClickDelete = async (id: string) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(t('応援コメントを削除しますか？'))) {
      const isSuccessful = await viewer.viewer.removeCompanyReference(id)
      if (isSuccessful) {
        setReferences(references.filter((r) => r.id !== id))
        // すでに編集ボタンをクリックしている場合、値がセットされているのでリッセット
        setEditingReference(null)
        messages.add({
          type: MessageType.Info,
          body: t('応援コメントを削除しました'),
          isTranslated: true,
          ttl: 5000,
        })
      }
    }
  }

  const onClickEdit = (reference: ICompanyReferenceBase) => {
    setEditingReference(reference)
    setTimeout(() => scroll(), 30)
  }

  return (
    <div className={styles.container}>
      {references.length === 0 || references.length === hiddenReferences.length ? (
        <div className={styles.noComments}>
          <p>{t('応援コメントがまだありません。')}</p>
          <p>
            {t('応援コメントを書くと、この会社の第三者評価となり、資金調達、人材採用、PRなどの加速に協力できます。')}
          </p>
        </div>
      ) : (
        <div className={styles.cardList}>
          {references.map((reference) => {
            const haveEditAndDelete = reference.commenter.username === viewer.viewer?.username
            // hide の場合表示
            if (reference.status === CompanyReferenceStatus.HIDDEN) {
              return null
            }

            return (
              <div key={reference.id} className={styles.cardItem}>
                <CompanyReferenceCard
                  item={reference}
                  onClickEdit={(targetReference: ICompanyReferenceBase) => onClickEdit(targetReference)}
                  onClickDelete={(id: string) => onClickDelete(id)}
                  isTwoColumn
                  haveEditAndDelete={haveEditAndDelete}
                />
              </div>
            )
          })}
        </div>
      )}

      {viewer.isInitialized && viewer.isSignedIn && commentable && (
        <div ref={newFormRef}>
          <CompanyReferenceForm
            reference={editingReference}
            references={references}
            setReferences={(newReferences: ICompanyReferenceBase[]) => setReferences(newReferences)}
            setEditingReference={(targetReference: ICompanyReferenceBase) => setEditingReference(targetReference)}
          />
        </div>
      )}
    </div>
  )
})
